import React, { useCallback, useState } from 'react';
import { ErrorMessage, SmallBodyText, SpaceWrapper } from 'ui-toolkit';
import { ActualIncomingDetailContainer } from '../../components/accounting/ActualIncomingDetailContainer';
import { ActualIncomingAchsSearchTable } from '../../components/accounting/ActualIncomingAchsSearchTable';
import { IncomingTransactionLayout } from '../../components/accounting/IncomingTransactionLayout';
import { IEscrowActualIncomingAchData } from '../../types/accounting/data/IEscrowActualIncomingAchData';
import { IEscrowExpectedIncomingAchData } from '../../types/accounting/data/IEscrowExpectedIncomingAchData';
import { eMatchedQueryValue } from '../../types/accounting/eMatchedQueryValue';
import { createAchQueryFromAmountCents } from '../../utilities/accounting/createQueryFromAmountCents';
import {
  convertExpectedIncomingAchTableQueryToApiQuery,
  IEscrowExpectedIncomingAchQuery,
} from '../../types/accounting/query/IEscrowExpectedIncomingAchQuery';
import { createDateRangeFromDays } from '../../utilities/accounting/createDateRangeFromDays';
import { IEscrowActualIncomingAchTableQuery } from '../../types/accounting/query/IEscrowActualIncomingAchTableQuery';
import { IEscrowExpectedIncomingAchTableQuery } from '../../types/accounting/query/IEscrowExpectedIncomingAchTableQuery';
import { ePermissions } from '../../types/administration/userAdministration/ePermissions';
import { useQueryStringQuery } from 'pages/useQueryStringQuery';
import {
  useDeleteMatchACHIncomingMutation,
  useGetACHIncomingActualQuery,
  useGetACHIncomingExpectedQuery,
  useGetOneACHIncomingActualQuery,
  usePostMatchACHIncomingMutation,
} from 'API/accounting/AccountingApi';
import { createDefaultPageData } from 'types/rest/IPageData';
import { convertToError } from 'API/BaseApi';
import { createIFetchDataStateFromPieces } from './createIFetchDataStateFromPieces';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { PermissionWrapper } from '../../components/accessControl/PermissionWrapper';
import { PermissionNotification } from '../../components/accessControl/PermissionNotification';
import { useHasPermissions } from '../../components/accessControl/hooks/useHasPermissions';

const SIZE_PER_PAGE = 25;

const defaultQuery: IEscrowActualIncomingAchTableQuery = {
  createdAt: createDateRangeFromDays(-60),
  matched: eMatchedQueryValue.UNMATCHED,
  pageNumber: 1,
  pageSize: SIZE_PER_PAGE,
  sortField: 'createdAt',
  sortOrder: 'desc',
};

const DetailArea = (selected: IEscrowActualIncomingAchData) => (
  <SpaceWrapper leftSpace={'none'} rightSpace={'none'}>
    {Object.keys(selected.achDetail).map((key) => (
      <SmallBodyText key={key} data-test-id={key}>
        {key.replace('_', '-')}: {selected.achDetail[key]}
      </SmallBodyText>
    ))}
  </SpaceWrapper>
);

export const IncomingAchsPage: React.FC = () => {
  const hasActualReadPermission = useHasPermissions([ePermissions.EscrowActualAchDepositsRead]);
  const hasExpectedReadPermission = useHasPermissions([ePermissions.EscrowExpectedAchPullDepositsRead]);
  const skip = !(hasActualReadPermission && hasExpectedReadPermission);

  const hasActualEditPermission = useHasPermissions([ePermissions.EscrowActualAchDepositsEdit]);
  const hasExpectedEditPermission = useHasPermissions([ePermissions.EscrowExpectedAchPullDepositsEdit]);

  const { queryObject, setQueryObject } = useQueryStringQuery('achs', defaultQuery);
  const { data = createDefaultPageData(), error, isFetching } = useGetACHIncomingActualQuery(queryObject, { skip });
  const fetchError = convertToError(error);

  const [suggestedQuery, setSuggestedQuery] = useState<IEscrowExpectedIncomingAchQuery>(
    {} as IEscrowExpectedIncomingAchQuery
  );

  const suggestedData = useGetACHIncomingExpectedQuery(suggestedQuery, { skip });
  const suggestedExpectedAch = createIFetchDataStateFromPieces({
    hookData: suggestedData,
    query: suggestedQuery,
    defaultData: createDefaultPageData<IEscrowExpectedIncomingAchData>(),
  });

  const setSuggestedExpectedQuery = useCallback(
    (query: IEscrowExpectedIncomingAchTableQuery) =>
      setSuggestedQuery(convertExpectedIncomingAchTableQueryToApiQuery(query)),
    [setSuggestedQuery]
  );
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const { data: selected } = useGetOneACHIncomingActualQuery(selectedId || '', { skip: !selectedId });

  const [doMatch, { error: postError }] = usePostMatchACHIncomingMutation();
  const onMatch = async (newMatch: IEscrowExpectedIncomingAchData) => {
    if (!selected || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await doMatch({ actualId: selected.id, expectedId: newMatch.id });

    checkForMutationSuccess(result, 'matching ACH');
  };

  const [deleteMatch, { error: deleteError }] = useDeleteMatchACHIncomingMutation();
  const onUnmatch = async (removeMatch: IEscrowExpectedIncomingAchData) => {
    if (!selected || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await deleteMatch({ actualId: selected.id, expectedId: removeMatch.id });

    checkForMutationSuccess(result, 'unmatching ACH');
  };

  return (
    <PermissionWrapper
      allowedPermissions={[ePermissions.EscrowActualAchDepositsRead, ePermissions.EscrowExpectedAchPullDepositsRead]}
      requireAll={true}
    >
      <PermissionNotification
        allowedPermissions={[ePermissions.EscrowActualAchDepositsEdit, ePermissions.EscrowExpectedAchPullDepositsEdit]}
        requireAll={true}
      />
      <IncomingTransactionLayout
        table={
          <>
            <ErrorMessage
              data-test-id={'main-error-location'}
              error={fetchError || convertToError(postError) || convertToError(deleteError)}
            />
            <ActualIncomingAchsSearchTable
              isQueryFetching={isFetching}
              data={data}
              detail={selected}
              query={queryObject}
              setSelectedId={setSelectedId}
              setQuery={setQueryObject}
            />
          </>
        }
        sidecar={
          <>
            {selected && hasExpectedReadPermission && (
              <ActualIncomingDetailContainer
                createQueryFromAmountCents={createAchQueryFromAmountCents}
                getExpectedDataFromData={(data) => data.matches || []}
                hasEditPermission={hasExpectedEditPermission && hasActualEditPermission}
                hasExpectedReadPermission={hasExpectedReadPermission}
                intro={'Select the matching deposit for this incoming ACH.'}
                onMatch={onMatch}
                onUnmatch={onUnmatch}
                selectedIncomingActual={selected}
                setSuggestedExpectedQuery={setSuggestedExpectedQuery}
                showDetailsOfSelected={DetailArea}
                suggestedExpectedResults={suggestedExpectedAch}
                type={'ach'}
              />
            )}
          </>
        }
      />
    </PermissionWrapper>
  );
};
